import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Scales,
  ShieldCheck,
  Handshake,
  ChartLineUp,
  FileText,
  Users,
  WhatsappLogo,
} from '@phosphor-icons/react';
import { FAQ } from '@/components/FAQ';
import { Button } from '@/components/ui/button';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const Home = () => {
  const faqItems = [
    {
      question: 'O escritório atende casos trabalhistas?',
      answer:
        'Sim. A RDC Advogados atua em demandas que envolvem verbas rescisórias, horas extras, vínculo de emprego, rescisão indireta, justa causa, assédio moral e outras irregularidades na relação de trabalho.',
    },
    {
      question: 'A RDC Advogados analisa negativa de plano de saúde?',
      answer:
        'Sim. Casos de negativa de cirurgia, exame, medicamento, tratamento, internação e demais questões relacionadas à cobertura assistencial podem ser analisados juridicamente.',
    },
    {
      question: 'O escritório atua com BPC/LOAS?',
      answer:
        'Sim. O escritório presta orientação e atua em questões relacionadas a pedido, negativa, suspensão, recurso e medidas cabíveis envolvendo o benefício assistencial.',
    },
    {
      question: 'O atendimento pode começar à distância?',
      answer:
        'Sim, a depender da natureza do caso e da documentação disponível. O atendimento pode ser iniciado de forma prática e organizada, sem prejuízo da análise técnica.',
    },
    {
      question: 'Onde fica a RDC Advogados?',
      answer:
        'A RDC Advogados está localizada na Rua São José, nº 46, Grupo 506/507, Centro - Rio de Janeiro/RJ, CEP: 20.010-020.',
    },
    {
      question: 'Como entrar em contato?',
      answer:
        'O contato pode ser feito por WhatsApp no número +55 21 2888-0258, pelo e-mail contato.rdcadvogados@gmail.com ou pelo Instagram @advogadosrdc.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-br from-[#2A2A2A] to-[#A98C67] text-white py-32 sm:py-40"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold playfair tracking-tight mb-6">
                RDC Advogados
              </h1>
              <p className="text-xl sm:text-2xl text-white/90 mb-4 leading-relaxed">
                Atuação jurídica estratégica em Direito Trabalhista, Plano de Saúde, BPC/LOAS e Defesa em Busca e Apreensão no Rio de Janeiro
              </p>
              <p className="text-base sm:text-lg text-white/70 mb-8 leading-relaxed">
                Atendimento jurídico com análise técnica, comunicação clara e condução responsável de cada caso. A RDC
                Advogados atua na defesa de direitos nas áreas trabalhista, da saúde, previdenciária e em defesa de busca e apreensão, com foco em
                orientação segura, estratégia consistente e seriedade institucional.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contato">
                  <Button
                    size="lg"
                    data-testid="hero-contact-button"
                    className="bg-white text-[#2A2A2A] hover:bg-white/90 rounded-sm font-medium px-8 py-6 text-base"
                  >
                    Solicitar análise do caso
                  </Button>
                </Link>
                <a
                  href="https://wa.me/552128880258"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="lg"
                    
                    data-testid="hero-whatsapp-button"
                    className="bg-[#25D366] text-white hover:bg-[#20BA5A] border-0 rounded-sm font-medium px-8 py-6 text-base"
                  >
                    <WhatsappLogo size={20} weight="bold" className="mr-2" />
                    Falar pelo WhatsApp
                  </Button>
                </a>
              </div>
              <p className="text-sm text-white/60 mt-6">
                Atendimento no Centro do Rio de Janeiro, com suporte jurídico objetivo desde o primeiro contato.
              </p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.6 }}>
              <img
                src="https://images.unsplash.com/photo-1571055931484-22dce9d6c510"
                alt="Escritório de advocacia premium"
                className="rounded-sm shadow-2xl w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Institutional Block */}
      <section className="py-24 bg-white" data-testid="institutional-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
              Clareza jurídica, estratégia consistente e atuação com credibilidade
            </h2>
            <p className="text-lg text-[#737373] leading-relaxed">
              A RDC Advogados foi estruturada para oferecer uma advocacia pautada por solidez técnica, leitura
              cuidadosa do caso concreto e comunicação acessível ao cliente. Em um cenário no qual muitas pessoas
              chegam ao escritório cercadas de dúvidas, urgência e insegurança, a atuação é conduzida com método,
              atenção aos detalhes e compromisso com orientação jurídica responsável.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <img
                src="https://images.unsplash.com/photo-1714268752727-2b93526ece94"
                alt="Rio de Janeiro"
                className="rounded-sm shadow-lg w-full h-auto"
              />
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.6 }}>
              <p className="text-base text-[#737373] leading-relaxed mb-4">
                Cada demanda é analisada de forma individual, considerando a realidade apresentada, os documentos
                disponíveis e a definição da medida juridicamente adequada. O propósito do escritório é oferecer ao
                cliente segurança, sobriedade e clareza, preservando a seriedade que questões sensíveis exigem.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Practice Areas - Bento Grid */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">Áreas de atuação</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              {...fadeUp}
              className="bg-white border border-[#2A2A2A]/10 rounded-sm p-8 hover:-translate-y-1 transition-transform duration-300 shadow-sm"
              data-testid="area-card-trabalhista"
            >
              <Scales size={48} weight="duotone" className="text-[#2A2A2A] mb-6" />
              <h3 className="text-2xl font-bold text-[#2A2A2A] playfair mb-4">Direito Trabalhista</h3>
              <p className="text-[#737373] leading-relaxed mb-6">
                Atuação em demandas relacionadas a verbas rescisórias, horas extras, vínculo de emprego, rescisão
                indireta, justa causa, assédio moral e demais irregularidades decorrentes da relação de trabalho.
              </p>
              <Link to="/trabalhista">
                <Button
                  variant="outline"
                  data-testid="area-button-trabalhista"
                  className="border-[#2A2A2A]/20 text-[#2A2A2A] hover:bg-[#FAFAFA] rounded-sm"
                >
                  Conhecer atuação trabalhista
                </Button>
              </Link>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="bg-white border border-[#2A2A2A]/10 rounded-sm p-8 hover:-translate-y-1 transition-transform duration-300 shadow-sm"
              data-testid="area-card-plano-saude"
            >
              <ShieldCheck size={48} weight="duotone" className="text-[#2A2A2A] mb-6" />
              <h3 className="text-2xl font-bold text-[#2A2A2A] playfair mb-4">Ação contra Plano de Saúde</h3>
              <p className="text-[#737373] leading-relaxed mb-6">
                Atuação em casos de negativa de cirurgia, exame, internação, medicamento, tratamento e demais recusas
                que possam comprometer o direito à saúde e a continuidade da assistência necessária.
              </p>
              <Link to="/plano-saude">
                <Button
                  variant="outline"
                  data-testid="area-button-plano-saude"
                  className="border-[#2A2A2A]/20 text-[#2A2A2A] hover:bg-[#FAFAFA] rounded-sm"
                >
                  Analisar negativa do plano
                </Button>
              </Link>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="bg-white border border-[#2A2A2A]/10 rounded-sm p-8 hover:-translate-y-1 transition-transform duration-300 shadow-sm"
              data-testid="area-card-bpc-loas"
            >
              <Handshake size={48} weight="duotone" className="text-[#2A2A2A] mb-6" />
              <h3 className="text-2xl font-bold text-[#2A2A2A] playfair mb-4">BPC/LOAS</h3>
              <p className="text-[#737373] leading-relaxed mb-6">
                Orientação jurídica em pedidos, negativas, suspensões, recursos e medidas cabíveis relacionadas ao
                benefício assistencial, com foco em idosos e pessoas com deficiência, sempre com atenção aos
                requisitos legais e ao contexto concreto do caso.
              </p>
              <Link to="/bpc-loas">
                <Button
                  variant="outline"
                  data-testid="area-button-bpc-loas"
                  className="border-[#2A2A2A]/20 text-[#2A2A2A] hover:bg-[#FAFAFA] rounded-sm"
                >
                  Saiba mais sobre BPC/LOAS
                </Button>
              </Link>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="bg-white border border-[#2A2A2A]/10 rounded-sm p-8 hover:-translate-y-1 transition-transform duration-300 shadow-sm"
              data-testid="area-card-busca-apreensao"
            >
              <ShieldCheck size={48} weight="duotone" className="text-[#2A2A2A] mb-6" />
              <h3 className="text-2xl font-bold text-[#2A2A2A] playfair mb-4">Busca e Apreensão</h3>
              <p className="text-[#737373] leading-relaxed mb-6">
                Defesa em ações de busca e apreensão de veículos e bens, com análise criteriosa da legalidade da medida, 
                revisão contratual e defesa dos direitos do cliente em processos judiciais.
              </p>
              <Link to="/busca-apreensao">
                <Button
                  variant="outline"
                  data-testid="area-button-busca-apreensao"
                  className="border-[#2A2A2A]/20 text-[#2A2A2A] hover:bg-[#FAFAFA] rounded-sm"
                >
                  Saiba mais sobre defesa
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Differentials */}
      <section className="py-24 bg-white" data-testid="differentials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">
              Uma atuação jurídica marcada por método, discrição e precisão
            </h2>
            <p className="text-lg text-[#737373] max-w-3xl mx-auto leading-relaxed">
              Na RDC Advogados, cada caso é conduzido a partir de três pilares centrais: análise técnica rigorosa,
              estratégia compatível com a situação apresentada e comunicação transparente com o cliente.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Users, title: 'Atendimento individualizado', desc: 'Leitura técnica e criteriosa do caso' },
              { icon: FileText, title: 'Comunicação clara e responsável', desc: 'Orientação transparente em todas as etapas' },
              { icon: ChartLineUp, title: 'Estratégia jurídica definida com precisão', desc: 'Planejamento adequado para cada situação' },
              { icon: Scales, title: 'Condução séria em demandas sensíveis', desc: 'Atenção ao contexto de cada cliente' },
              { icon: ShieldCheck, title: 'Postura institucional sólida e confiável', desc: 'Seriedade que questões importantes exigem' },
              { icon: Handshake, title: 'Análise técnica rigorosa', desc: 'Exame profundo de cada elemento do caso' },
            ].map((item, index) => (
              <motion.div
                key={item.title || item.id || index}
                {...fadeUp}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="flex flex-col items-start"
                data-testid={`differential-${index}`}
              >
                <item.icon size={40} weight="duotone" className="text-[#A98C67] mb-4" />
                <h3 className="text-lg font-semibold text-[#2A2A2A] mb-2">{item.title}</h3>
                <p className="text-[#737373]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">
              Como funciona o atendimento
            </h2>
            <p className="text-lg text-[#737373]">Atendimento com organização, clareza e estratégia</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Contato inicial',
                desc: 'O caso é apresentado com os principais fatos e documentos disponíveis, permitindo a compreensão inicial da situação.',
              },
              {
                step: '2',
                title: 'Análise jurídica',
                desc: 'O escritório realiza a leitura técnica do contexto, examina a documentação e identifica os pontos juridicamente relevantes.',
              },
              {
                step: '3',
                title: 'Orientação estratégica',
                desc: 'Após a análise, o cliente recebe orientação clara sobre cenário jurídico, possibilidades, riscos e caminhos adequados para o caso.',
              },
              {
                step: '4',
                title: 'Condução da medida cabível',
                desc: 'Quando necessário, são adotadas as providências administrativas ou judiciais pertinentes, com acompanhamento técnico e comunicação responsável.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title || item.id || index}
                {...fadeUp}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="text-center"
                data-testid={`step-${index}`}
              >
                <div className="w-16 h-16 bg-[#2A2A2A] text-white rounded-full flex items-center justify-center text-2xl font-bold playfair mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-[#2A2A2A] playfair mb-3">{item.title}</h3>
                <p className="text-[#737373] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-24 bg-white" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-6">
                RDC Advogados
              </h2>
              <p className="text-lg text-[#737373] leading-relaxed mb-6">
                Com foco em uma advocacia técnica, acessível e estrategicamente estruturada, o escritório atua em áreas
                nas quais o cliente, muitas vezes, precisa de resposta segura em momentos de elevada sensibilidade.
              </p>
              <p className="text-lg text-[#737373] leading-relaxed mb-8">
                A proposta da RDC Advogados é oferecer uma atuação firme, bem fundamentada e conduzida com sobriedade,
                sem excessos retóricos e sem promessas incompatíveis com a ética profissional.
              </p>
              <Link to="/sobre">
                <Button
                  data-testid="about-button"
                  className="bg-[#2A2A2A] text-white hover:bg-[#A98C67] rounded-sm font-medium px-8 py-3"
                >
                  Conhecer o escritório
                </Button>
              </Link>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2, duration: 0.6 }}>
              <img
                src="https://images.pexels.com/photos/175045/pexels-photo-175045.jpeg"
                alt="Assinatura de contrato"
                className="rounded-sm shadow-lg w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2A2A2A] playfair mb-4">Perguntas frequentes</h2>
          </motion.div>
          <motion.div {...fadeUp}>
            <FAQ items={faqItems} />
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#2A2A2A] text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold playfair mb-6">
              Seu caso merece análise técnica, direção segura e atendimento responsável
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Se você busca orientação jurídica em matéria trabalhista, de saúde, previdenciária ou defesa em busca e apreensão, a RDC Advogados
              está preparada para avaliar a sua situação com seriedade, clareza e consistência estratégica.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button
                  size="lg"
                  data-testid="cta-contact-button"
                  className="bg-white text-[#2A2A2A] hover:bg-white/90 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Solicitar atendimento
                </Button>
              </Link>
              <a href="https://wa.me/552128880258" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  
                  data-testid="cta-whatsapp-button"
                  className="bg-[#25D366] text-white hover:bg-[#20BA5A] border-0 rounded-sm font-medium px-8 py-6 text-base"
                >
                  Falar com o escritório
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;